/* eslint-disable camelcase */
import * as React from 'react';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import type { PeerReviewAi_UserFeedbackChoice } from '__generated__/graphql-types';
import type { PeerReviewAi_ReviewPartUserFeedback } from '__generated__/graphql-types-do-not-use';

import type {
  GetReviewUserFeedbacksBySubmissionQuery,
  GetReviewUserFeedbacksBySubmissionQueryVariables,
} from 'bundles/peer/api/__generated__/getReviewUserFeedbacksBySubmission';
import type {
  PeerReviewAiSaveUserFeedbackMutation,
  PeerReviewAiSaveUserFeedbackMutationVariables,
} from 'bundles/peer/api/__generated__/peerReviewAiSaveUserFeedback';
import getReviewUserFeedbacksBySubmission from 'bundles/peer/api/getReviewUserFeedbacksBySubmission.graphql';
import peerReviewAiSaveUserFeedback from 'bundles/peer/api/peerReviewAiSaveUserFeedback.graphql';

export type AiFeedbackRatingProviderProps = {
  isAIGraded: boolean;
  courseId: string;
  itemId: string;
  submissionId?: string;
  children: ReactNode;
};

export type AiFeedbackRatingContextType = {
  userFeedbackBySubmission: {
    loading: boolean;
    // key is reviewId
    reviewPartUserFeedbacksByReviewId?: Record<string, PeerReviewAi_ReviewPartUserFeedback[]>;
  };
  actions: {
    saveUserFeedback: (
      feedbackChoice: PeerReviewAi_UserFeedbackChoice | null,
      reviewId: string,
      reviewPartId: string
    ) => void;
    saveUserFeedbackLoading: boolean;
  };
};

const AiFeedbackRatingContext = createContext<AiFeedbackRatingContextType | undefined>(undefined);

export const useAiFeedbackRatingContext = () => {
  const context = useContext(AiFeedbackRatingContext);
  if (context === undefined) {
    throw new Error(
      '`useAiFeedbackRatingContext` was used outside of `AiFeedbackRatingProvider`. Ensure your component is nested under `<AiFeedbackRatingProvider>`.'
    );
  }
  return context;
};

const AiFeedbackRatingProvider = ({
  isAIGraded,
  courseId,
  itemId,
  submissionId,
  children,
}: AiFeedbackRatingProviderProps) => {
  const [reviewPartUserFeedbacksByReviewId, setReviewPartUserFeedbacksByReviewId] = useState<
    Record<string, PeerReviewAi_ReviewPartUserFeedback[]>
  >({});

  /**
   * Handles onComPleted state of fetching user feedbacks
   *
   * @param data
   */
  const handleGetReviewUserFeedbackOnCompleted = (data: GetReviewUserFeedbacksBySubmissionQuery) => {
    if (data.PeerReviewAi?.getReviewUserFeedbacksBySubmission) {
      // remap with review id as key to easily query downstream
      setReviewPartUserFeedbacksByReviewId(
        data.PeerReviewAi.getReviewUserFeedbacksBySubmission.reduce(
          (accumulator, actual) => ({ ...accumulator, [actual.reviewId]: actual.reviewPartUserFeedbacks }),
          {}
        )
      );
    }
  };

  // query to fetch all user feedbacks for all ai feedbacks on a submission
  const [getReviewUserFeedback, reviewUserFeedbackStatus] = useLazyQuery<
    GetReviewUserFeedbacksBySubmissionQuery,
    GetReviewUserFeedbacksBySubmissionQueryVariables
  >(getReviewUserFeedbacksBySubmission, {
    onCompleted: handleGetReviewUserFeedbackOnCompleted,
    context: { clientName: 'gatewayGql' },
  });

  useEffect(() => {
    // if this is ai grading, retrieve all the ai user feedback for this submission if any available
    if (isAIGraded && submissionId) {
      const getReviewUserFeedbackBySubmission = async () => {
        await getReviewUserFeedback({
          variables: {
            courseId,
            itemId,
            submissionId,
          },
        });
      };

      getReviewUserFeedbackBySubmission();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  // mutation to save user feedback on ai comments
  const [saveUserFeedback, saveUserFeedbackStatus] = useMutation<
    PeerReviewAiSaveUserFeedbackMutation,
    PeerReviewAiSaveUserFeedbackMutationVariables
  >(peerReviewAiSaveUserFeedback, {
    context: { clientName: 'gatewayGql' },
  });

  /**
   * Applies user feedback choice on ai comments
   *
   * @param feedbackChoice
   * @param reviewId
   * @param reviewPartId
   */
  const applySaveUserFeedback = async (
    feedbackChoice: PeerReviewAi_UserFeedbackChoice | null,
    reviewId: string,
    reviewPartId: string
  ) => {
    if (isAIGraded && courseId && itemId && submissionId) {
      await saveUserFeedback({
        variables: {
          input: {
            courseId,
            feedbackChoice,
            itemId,
            reviewId,
            reviewPartId,
            submissionId,
          },
        },
      });
    }
  };

  return (
    <AiFeedbackRatingContext.Provider
      value={{
        userFeedbackBySubmission: {
          loading: reviewUserFeedbackStatus.loading,
          reviewPartUserFeedbacksByReviewId,
        },
        actions: {
          saveUserFeedback: applySaveUserFeedback,
          saveUserFeedbackLoading: saveUserFeedbackStatus.loading,
        },
      }}
    >
      {children}
    </AiFeedbackRatingContext.Provider>
  );
};

export default AiFeedbackRatingProvider;
